import { useMutation } from '@tanstack/react-query';
import { setSelfReportedRegistrationApiV1EventsEventIdSelfReportedRegistrationPut } from '@api-client/api-client';
import type { SelfReportedRegistration } from '@models/api';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';

export interface SelfReportedRegistrationVariables {
  eventId: string;
  answer: SelfReportedRegistration;
}

/**
 * Saves a Going member's answer to the Going celebration's "have a ticket?" question.
 *
 * Mounted by CelebrationHost, which outlives the drawer: "I need a ticket" fires this without
 * awaiting it (an await before opening the ticket site makes iOS Safari block the popup), then
 * closes the drawer, so the mutation must not belong to a component that is about to unmount.
 *
 * No toast on failure: the member is still Going, the question was a courtesy, and the server asks
 * again on the next Going. No cache refresh on success: nothing client-side reads the answer.
 */
export function useSelfReportedRegistration() {
  return useMutation({
    mutationFn: ({ eventId, answer }: SelfReportedRegistrationVariables) =>
      setSelfReportedRegistrationApiV1EventsEventIdSelfReportedRegistrationPut(eventId, {
        self_reported_registration: answer,
      }),
    onError: error => {
      logger.warn('Failed to save the ticket answer:', sanitizeError(error));
    },
  });
}
